// deps
import React, { ReactElement, useContext, memo, useMemo, useCallback } from "react";

// internal deps
import { PaymentAlert } from "@swiggy-private/payment-ui";
import { format } from "@swiggy-private/core-ui";
import { CardDetails, PaymentAlertActionProps, PaymentFallbacks } from "@swiggy-private/payment-types";

// contexts
import { CheckoutContext, CheckoutActionTypes, FailedTransactionActionPayload } from "@swiggy-private/checkout-core";

// constants
import * as TextDict from "@payment-client/constants/TextDict";

// helpers
import { getTransactionFallbackActions } from "@payment-client/helpers/TransactionFallbackHelper";
import { PAYMENT_FALLBACK_TYPES } from "@payment-client/constants/PaymentFallback";

export interface TransactionFailedAlertProps {
    payAction: (cardDetails: CardDetails, methodName: string, additionalParams: { [key: string]: string }) => void;
    amountToPay: string;
    payload: FailedTransactionActionPayload;
}

export const DEFAULT_PAYMENT_FALLBACK_DATA: PaymentFallbacks = {
    failureTitle: "Looks like the payment didn’t go through!",
    failureMessage: "Any amount deducted will be refunded within 2-4 days. How would you like to proceed?",
    failureIcon: TextDict.ICON_PAYMENT_FAILED_V4,
    paymentMethods: [
        {
            name: PAYMENT_FALLBACK_TYPES.RETRY,
            displayName: "Try Again",
            paymentCode: PAYMENT_FALLBACK_TYPES.RETRY,
        },
        {
            name: PAYMENT_FALLBACK_TYPES.DISMISS,
            displayName: "Dismiss",
            paymentCode: PAYMENT_FALLBACK_TYPES.DISMISS,
        },
    ],
};

function TransactionFailedAlert(props: TransactionFailedAlertProps): ReactElement | null {
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const { transactionReqData } = props.payload;
    const fallbackData: PaymentFallbacks = DEFAULT_PAYMENT_FALLBACK_DATA;
    const { failureMessage, failureTitle, failureIcon, paymentMethods } = fallbackData;
    const info = format(TextDict.PAYMENT_ALERT_MAIN_TEXT, props.amountToPay);
    const { payAction } = props;

    // Handles failure alert action click based on fallback payment code
    const onOverlayClose = useCallback(() => {
        setCheckoutState({ type: CheckoutActionTypes.SECURE_CARD });
    }, [setCheckoutState]);

    const fallbackActions: PaymentAlertActionProps[] = useMemo(
        () =>
            getTransactionFallbackActions({
                payAction,
                transactionReqData,
                setCheckoutState,
                fallbackMethods: paymentMethods,
            }),
        [payAction, paymentMethods, transactionReqData, setCheckoutState],
    );

    if (checkoutState.type !== CheckoutActionTypes.TRANSACTION_FAILED) {
        return null;
    }

    return (
        <PaymentAlert
            icon={failureIcon}
            title={failureTitle}
            message={failureMessage}
            info={info}
            primaryAction={fallbackActions[0]}
            secondaryAction={fallbackActions[1]}
            hideOnOverlayClick={true}
            onClose={onOverlayClose}
        />
    );
}

export default memo(TransactionFailedAlert);
